import { LiveEvent, Palimpsest, MarketConfig } from "./liveSlice";
import {
  removeDuplicates,
  sortDataByCategoryOrderLabelAttribute as originalSortByCategoryOrderLabel,
  sortDataByOrderAndLabelAttribute,
  sortDataByOrderAttribute,
} from "services/utils";

interface IComposedLiveData {
  composedData: {
    sportsObj: Record<string, number[]>;
    categoriesObj: Record<string, number[]>;
    tournamentsObj: Record<string, Record<string, number>[]>;
  };
  orderedData: {
    orderedSports: Palimpsest[];
  };
}

// Stable sort implementation for cross-browser consistency
const stableSort = <T>(array: T[], compareFn: (a: T, b: T) => number): T[] => {
  const stabilized = array.map((el, index) => ({ el, index }));
  stabilized.sort((a, b) => {
    const order = compareFn(a.el, b.el);
    if (order !== 0) return order;
    return a.index - b.index;
  });
  return stabilized.map((entry) => entry.el);
};

// Improved consistent sorting function
const sortDataByCategoryOrderLabelAttribute = (
  data: Palimpsest[]
): Palimpsest[] => {
  if (!data || data.length === 0) return [];

  return stableSort([...data], (a, b) => {
    // First by category order
    const categoryOrderDiff = (a.order || 0) - (b.order || 0);
    if (categoryOrderDiff !== 0) return categoryOrderDiff;

    // Then by category label
    const categoryLabelA = a.categoryLabel?.toLowerCase() || "";
    const categoryLabelB = b.categoryLabel?.toLowerCase() || "";
    const labelCompare = categoryLabelA.localeCompare(categoryLabelB);
    if (labelCompare !== 0) return labelCompare;

    // Finally by tournament order
    return (a.order || 0) - (b.order || 0);
  });
};

export const composeLiveData = (
  events: Record<string, LiveEvent>,
  sports: Record<string, Palimpsest>,
  isVideoFilter: boolean
): IComposedLiveData => {
  const sportsObj: Record<string, number[]> = {};
  const categoriesObj: Record<string, number[]> = {};
  const tournamentsObj: Record<string, Record<string, number>[]> = {};

  Object.values(events).forEach((event) => {
    if (isVideoFilter && event.stream_id === 0) return;

    const eventSportId = event.sport_id;
    const eventCategoryId = event.category_id;

    // Update sportsObj
    sportsObj[eventSportId] = sportsObj[eventSportId]
      ? removeDuplicates([...sportsObj[eventSportId], eventCategoryId])
      : [eventCategoryId];

    // Update categoriesObj
    categoriesObj[eventCategoryId] = categoriesObj[eventCategoryId]
      ? removeDuplicates([
          ...categoriesObj[eventCategoryId],
          event.tournament_id,
        ])
      : [event.tournament_id];

    // Update tournamentsObj
    const tournamentEntry = {
      id: event.id,
      sportId: event.sport_id,
      categoryId: event.category_id,
      begin: event.begin,
    };

    tournamentsObj[event.tournament_id] = tournamentsObj[event.tournament_id]
      ? [...tournamentsObj[event.tournament_id], tournamentEntry]
      : [tournamentEntry];
  });

  // Sort sports by order attribute
  const orderedSportArray = Object.keys(sportsObj)
    .map((key) => sports[key])
    .filter(Boolean);

  return {
    composedData: { sportsObj, categoriesObj, tournamentsObj },
    orderedData: {
      orderedSports: sortDataByOrderAttribute(orderedSportArray),
    },
  };
};

export const orderCategories = (
  categories: Record<string, Palimpsest>,
  composedSports: Record<string, number[]>,
  selectedSport: number | undefined
): Palimpsest[] | undefined => {
  if (!selectedSport || !composedSports[selectedSport] || !categories) return;

  const orderedCategoryArray = composedSports[selectedSport]
    .map((categoryId) => categories[categoryId])
    .filter(Boolean);

  return sortDataByOrderAttribute(orderedCategoryArray);
};

export const orderTournaments = (
  categories: Record<string, Palimpsest>,
  orderedCategories: Palimpsest[],
  tournaments: Record<string, Palimpsest>,
  composedCategories: Record<string, number[]>,
  selectedCategory: string | number
): Palimpsest[] | undefined => {
  if (!categories || !composedCategories || !tournaments) return;

  if (selectedCategory === "all") {
    const allOrderedTournamentArray: Palimpsest[] = [];

    const sortedCategories =
      sortDataByOrderAndLabelAttribute(orderedCategories);

    for (const category of sortedCategories) {
      if (!category.id || !composedCategories[category.id]) continue;

      const sortedTournamentIds = [...composedCategories[category.id]].sort(
        (a, b) => {
          return (tournaments[a]?.order || 0) - (tournaments[b]?.order || 0);
        }
      );

      for (const tournamentId of sortedTournamentIds) {
        if (tournaments[tournamentId]) {
          allOrderedTournamentArray.push({
            ...tournaments[tournamentId],
            categoryLabel: categories[category.id]?.label,
            order: categories[category.id]?.order,
          });
        }
      }
    }

    return sortDataByCategoryOrderLabelAttribute(allOrderedTournamentArray);
  }

  if (selectedCategory && composedCategories[selectedCategory]) {
    const tournamentIds = [...composedCategories[selectedCategory]];

    const sortedTournamentIds = tournamentIds.sort((a, b) => {
      return (tournaments[a]?.order || 0) - (tournaments[b]?.order || 0);
    });

    const orderedTournamentArray = sortedTournamentIds
      .map((tournamentId) => ({
        ...tournaments[tournamentId],
        categoryLabel: categories[selectedCategory]?.label,
        categoryOrder: categories[selectedCategory]?.order,
      }))
      .filter(Boolean);

    return sortDataByCategoryOrderLabelAttribute(orderedTournamentArray);
  }
};

export const composeOverviewMarkets = (
  markets: Record<string, MarketConfig>,
  selectedSport: number | undefined
): MarketConfig[] => {
  if (!selectedSport) return [];

  return Object.values(markets)
    .filter((market) => market.sport_id === selectedSport && market.main)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

export const buildSearchData = (
  events: Record<string, LiveEvent>,
  sports: Record<string, Palimpsest>,
  tournaments: Record<string, Palimpsest>
): Record<string, LiveEvent[]> => {
  const searchDataObject: Record<string, LiveEvent[]> = {};

  Object.values(events).forEach((event) => {
    const sport = sports[event.sport_id];
    if (!sport) return;

    const newEvent = {
      ...event,
      tournamentLabel: tournaments[event.tournament_id]?.label,
    };

    if (searchDataObject[sport.label]) {
      searchDataObject[sport.label].push(newEvent);
    } else {
      searchDataObject[sport.label] = [newEvent];
    }
  });

  return searchDataObject;
};

export const findSearchResults = (
  searchData: Record<string, LiveEvent[]>,
  searchValue: string
): Record<string, LiveEvent[]> => {
  const lowerSearchValue = searchValue.toLowerCase();

  return Object.entries(searchData).reduce((acc, [sportLabel, events]) => {
    const filteredEvents = events.filter((event) => {
      const eventLabel = `${event.home} - ${event.away}`.toLowerCase();
      const tournamentLabel = event.tournamentLabel?.toLowerCase() || "";

      return (
        eventLabel.includes(lowerSearchValue) ||
        tournamentLabel.includes(lowerSearchValue)
      );
    });

    if (filteredEvents.length > 0) {
      acc[sportLabel] = filteredEvents;
    }

    return acc;
  }, {} as Record<string, LiveEvent[]>);
};
